import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatWhen } from '@caios/ui';
import './article-history.css';

/**
 * An article's history (2026-11-03, SOC 2): every version the database recorded, who made it and when, and exactly what
 * changed — shown as additions and deletions, word by word. The team can put an earlier version back (it becomes a new
 * version; nothing is erased); a client admin can load one into their working copy.
 */
export type Version = { version: number; title: string; meta_description: string | null; body: string; word_count: number; who: string; actor: string; at: string };
type Event = { kind: string; actor_kind: string; who: string; detail: Record<string, unknown>; at: string };
type History = { versions: Version[]; events: Event[] };

const EVENT: Record<string, string> = { paragraph_edited: 'reworded a paragraph', paragraph_reverted: 'put a paragraph back', note_added: 'left a note', note_withdrawn: 'withdrew a note',
  edits_applied: 'had their wording applied', approved: 'approved it', changes_requested: 'asked for changes', published: 'published it', publish_failed: 'tried to publish — it did not go out',
  declined: 'declined it', restored: 'restored it', scheduled: 'scheduled it on WordPress', saved_draft: 'saved it to WordPress as a draft', draft_saved: 'saved edits',
  draft_discarded: 'discarded their edits', sent_to_team: 'sent their changes to the team', version_restored: 'put an earlier version back', refresh_drafted: 'drafted a refresh' };

/** Word-level difference of two texts: paragraphs are matched first, then words inside a changed paragraph. */
type Part = { t: string; k: 'same' | 'add' | 'del' };
function lcs<T>(a: T[], b: T[]): [number, number][] {
  const n = a.length, m = b.length, dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
  const out: [number, number][] = [];
  let i = 0, j = 0;
  while (i < n && j < m) { if (a[i] === b[j]) { out.push([i, j]); i++; j++; } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) i++; else j++; }
  return out;
}
function wordDiff(a: string, b: string): Part[] {
  const A = a.split(/(\s+)/), B = b.split(/(\s+)/);
  if (A.length * B.length > 4_000_000) return [{ t: a, k: 'del' }, { t: b, k: 'add' }];
  const pairs = lcs(A, B), out: Part[] = [];
  let i = 0, j = 0;
  for (const [x, y] of [...pairs, [A.length, B.length] as [number, number]]) {
    if (i < x) out.push({ t: A.slice(i, x).join(''), k: 'del' });
    if (j < y) out.push({ t: B.slice(j, y).join(''), k: 'add' });
    if (x < A.length) out.push({ t: A[x]!, k: 'same' });
    i = x + 1; j = y + 1;
  }
  return out;
}
/** Paragraph blocks of the difference: unchanged ones folded away, changed ones shown word by word. */
export function diffBlocks(before: string, after: string): { parts: Part[]; changed: boolean }[] {
  const A = before.split(/\n{2,}/), B = after.split(/\n{2,}/), pairs = lcs(A, B), out: { parts: Part[]; changed: boolean }[] = [];
  let i = 0, j = 0;
  for (const [x, y] of [...pairs, [A.length, B.length] as [number, number]]) {
    const del = A.slice(i, x), add = B.slice(j, y);
    for (let k = 0; k < Math.max(del.length, add.length); k++) {
      const d = del[k], a = add[k];
      out.push({ changed: true, parts: d !== undefined && a !== undefined ? wordDiff(d, a) : d !== undefined ? [{ t: d, k: 'del' }] : [{ t: a!, k: 'add' }] });
    }
    if (x < A.length) out.push({ changed: false, parts: [{ t: A[x]!, k: 'same' }] });
    i = x + 1; j = y + 1;
  }
  return out;
}
export function Diff({ before, after }: { before: string; after: string }) {
  const blocks = diffBlocks(before, after);
  if (!blocks.some((b) => b.changed)) return <p className="ah-quiet">No change to the text.</p>;
  return <div className="ah-diff">{blocks.map((b, i) => b.changed
    ? <p key={i}>{b.parts.map((p, k) => p.k === 'same' ? <span key={k}>{p.t}</span> : p.k === 'add' ? <ins key={k}>{p.t}</ins> : <del key={k}>{p.t}</del>)}</p>
    : (blocks[i - 1]?.changed || blocks[i + 1]?.changed) ? <p key={i} className="ah-context">{b.parts[0]!.t.slice(0, 220)}{b.parts[0]!.t.length > 220 ? '…' : ''}</p> : null)}</div>;
}

export function ArticleHistory({ url, onRestore, restoreLabel }: { url: string; onRestore?: ((v: Version) => void) | undefined; restoreLabel?: string }) {
  const q = useQuery({ queryKey: ['article-history', url], queryFn: async () => { const r = await fetch(url, { credentials: 'include' }); if (!r.ok) throw new Error(String(r.status)); return (await r.json()) as History; } });
  const [pick, setPick] = useState<number | null>(null);
  if (!q.data) return <p className="ah-quiet">{q.isError ? 'Could not load the history.' : 'Loading the history…'}</p>;
  const { versions, events } = q.data;
  const current = pick ?? versions[0]?.version ?? null;
  const v = versions.find((x) => x.version === current), prev = versions.find((x) => x.version === (current ?? 0) - 1);
  const items = [...versions.map((x) => ({ at: x.at, key: `v${x.version}`, version: x.version, who: x.who, text: x.version === 1 && !prev ? 'first version' : `saved version ${x.version}` })),
    ...events.map((e, i) => ({ at: e.at, key: `e${i}`, version: null as number | null, who: e.who, text: EVENT[e.kind] ?? e.kind.replace(/_/g, ' ') }))]
    .sort((a, b) => b.at.localeCompare(a.at));
  return (
    <div className="ah" data-testid="article-history">
      <ol className="ah-log">
        {items.map((it) => <li key={it.key}>
          {it.version ? <button type="button" aria-pressed={current === it.version} onClick={() => setPick(it.version)} data-testid="ah-version"><b>{it.who}</b> {it.text}<small>{formatWhen(it.at)}</small></button>
            : <div className="ah-event"><b>{it.who}</b> {it.text}<small>{formatWhen(it.at)}</small></div>}
        </li>)}
      </ol>
      <section className="ah-detail">
        {v ? <>
          <div className="ah-head"><div><b>{prev ? `Version ${prev.version} → ${v.version}` : `Version ${v.version}`}</b><small>{v.who} · {formatWhen(v.at)} · {v.word_count.toLocaleString()} words</small></div>
            {onRestore && v.version !== versions[0]?.version && <button type="button" className="cx-btn cx-btn--sm" onClick={() => onRestore(v)} data-testid="ah-restore">{restoreLabel ?? `Restore version ${v.version}`}</button>}</div>
          {prev && prev.title !== v.title && <p className="ah-field"><span>Title</span>{wordDiff(prev.title, v.title).map((p, k) => p.k === 'same' ? <span key={k}>{p.t}</span> : p.k === 'add' ? <ins key={k}>{p.t}</ins> : <del key={k}>{p.t}</del>)}</p>}
          {prev && (prev.meta_description ?? '') !== (v.meta_description ?? '') && <p className="ah-field"><span>Search description</span>{wordDiff(prev.meta_description ?? '', v.meta_description ?? '').map((p, k) => p.k === 'same' ? <span key={k}>{p.t}</span> : p.k === 'add' ? <ins key={k}>{p.t}</ins> : <del key={k}>{p.t}</del>)}</p>}
          {prev ? <Diff before={prev.body} after={v.body} /> : <p className="ah-quiet">The first version recorded: {v.title}</p>}
        </> : <p className="ah-quiet">No versions yet.</p>}
        <p className="ah-seal">This history can’t be edited or deleted — by a client or by the team. Putting an old version back adds a new entry; it never erases one.</p>
      </section>
    </div>
  );
}
